import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { TrendingUp, Star, ArrowRight, Flame } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useCart } from '@/context/CartContext';
import { toast } from 'sonner';
import AfterpayPriceTag from '@/components/AfterpayPriceTag';
import { LoyaltyPointsBadge } from '@/components/LoyaltyPointsBadge';

const popularTreatments = [
  {
    id: 'pop-full-upper',
    rank: 1,
    name: 'Full Upper Face — Botox',
    subtitle: '7 Areas: Forehead, 11s, Crow\'s Feet, Brow Lift, Smile Lift, Lip Flip, Bunny Lines',
    price: 599,
    tag: '#1 Most Booked',
    color: 'from-violet-600 to-purple-700',
    tagColor: 'bg-violet-500',
    href: '/botox-ottawa',
    description: 'Our most popular treatment — unlimited Botox units across all 7 upper face areas. One appointment, total refresh. No unit counting, no surprise charges.',
    cartType: 'botox',
  },
  {
    id: 'pop-nefertiti-booster',
    rank: 2,
    name: 'Nefertiti Lift + Skin Booster',
    subtitle: 'Jawline & Neck Tightening + 3ML Redensity 1',
    price: 899,
    tag: 'Trending Now',
    color: 'from-amber-600 to-yellow-600',
    tagColor: 'bg-amber-500',
    href: '/skin-boosters',
    description: 'The ultimate neck and jawline combo — Nefertiti Botox lifts and tightens the jawline and neck, while Redensity 1 skin booster restores deep hydration and glow from within.',
    cartType: 'combo',
  },
  {
    id: 'pop-kiss-me-lips',
    rank: 3,
    name: 'Kiss Me Lips',
    subtitle: 'Full Syringe Dermal Filler + FREE Lip Flip',
    price: 499,
    tag: 'Client Favorite',
    color: 'from-rose-500 to-pink-600',
    tagColor: 'bg-rose-500',
    href: '/dermal-fillers',
    description: 'The perfect pout — a full syringe of premium lip filler for volume and shape, plus a complimentary Botox lip flip for that beautiful upward curl. The combination everyone is asking for.',
    cartType: 'filler',
  },
];

const MostPopularSection = () => {
  const { addToCart, isInCart } = useCart();

  const handleAdd = (t) => {
    const cartItem = {
      id: t.id,
      name: t.name,
      category: t.subtitle,
      price: `$${t.price}`,
      type: t.cartType,
      duration: 45,
    };
    if (!isInCart(t.id)) {
      addToCart(cartItem);
      toast.success(`${t.name} added to cart!`, { duration: 3000 });
    }
  };

  return (
    <section className="py-16 sm:py-20 bg-gradient-to-b from-stone-950 to-stone-900 relative overflow-hidden" data-testid="most-popular-section">
      <div className="absolute inset-0 opacity-[0.03]" style={{ backgroundImage: 'radial-gradient(circle at 1px 1px, #D4AF37 1px, transparent 0)', backgroundSize: '40px 40px' }} />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6">
        {/* Header */}
        <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-center mb-12">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full mb-5" style={{ backgroundColor: 'rgba(212,175,55,0.15)', border: '1px solid rgba(212,175,55,0.3)' }}>
            <Flame className="w-4 h-4 text-amber-400" />
            <span className="text-amber-400 text-xs font-bold uppercase tracking-[0.2em]">Trending This Month</span>
          </div>
          <h2 className="text-3xl sm:text-4xl text-white mb-3" style={{ fontFamily: "'Playfair Display', serif" }}>
            Most Popular <span className="italic text-amber-400">Right Now</span>
          </h2>
          <p className="text-stone-400 max-w-xl mx-auto">The treatments our clients are booking most — based on real appointments this month.</p>
        </motion.div>

        {/* Cards — Stacked Vertically */}
        <div className="flex flex-col gap-6 max-w-2xl mx-auto">
          {popularTreatments.map((t, i) => {
            const inCart = isInCart(t.id);
            return (
              <motion.div
                key={t.id}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.12 }}
                className="relative bg-stone-800/60 border border-stone-700/50 rounded-2xl overflow-hidden backdrop-blur-sm hover:border-stone-600 transition-all group"
              >
                {/* Rank + Tag */}
                <div className={`${t.tagColor} px-4 py-2.5 flex items-center justify-between`}>
                  <span className="text-white text-xs font-bold uppercase tracking-wider flex items-center gap-1.5">
                    <TrendingUp className="w-3.5 h-3.5" /> {t.tag}
                  </span>
                  <span className="text-white/80 text-xs font-mono">#{t.rank}</span>
                </div>

                <div className="p-6 sm:flex sm:items-center sm:gap-6">
                  <div className="flex-1">
                    <h3 className="text-xl font-bold text-white mb-1" style={{ fontFamily: "'Playfair Display', serif" }}>{t.name}</h3>
                    <p className="text-stone-400 text-sm mb-3">{t.subtitle}</p>

                    <div className="flex items-baseline gap-2 mb-2">
                      <span className="text-3xl font-bold text-white">${t.price.toLocaleString()}</span>
                      <span className="text-stone-500 text-sm">+tax</span>
                    </div>
                    <div className="flex items-center gap-3 mb-3">
                      <AfterpayPriceTag price={t.price} treatment={t.title || t.name} className="[&_span:last-child]:text-stone-400" />
                      <LoyaltyPointsBadge price={t.price} className="text-amber-400 [&_svg]:fill-amber-400 [&_svg]:text-amber-400" />
                    </div>

                    <p className="text-stone-400 text-sm leading-relaxed">{t.description}</p>
                  </div>

                  <div className="flex gap-2 mt-4 sm:mt-0 sm:flex-col sm:w-40 flex-shrink-0">
                    <Button
                      onClick={() => handleAdd(t)}
                      className={`flex-1 sm:w-full py-5 font-semibold ${inCart ? 'bg-green-600 hover:bg-green-700' : 'bg-white text-stone-900 hover:bg-stone-100'}`}
                      data-testid={`popular-add-${t.id}`}
                    >
                      {inCart ? 'Added' : 'Add to Cart'}
                    </Button>
                    <Link to={t.href}>
                      <Button variant="outline" className="border-stone-600 text-stone-300 hover:bg-stone-700 py-5 px-4 sm:w-full">
                        <ArrowRight className="w-4 h-4" />
                      </Button>
                    </Link>
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default MostPopularSection;
